/* Iter 191 — Standalone full-page view of the Today's Invoices report.
 * Opened via the "Open in new tab ↗" button in the Order Register popup
 * or by navigating to /today-invoices directly. Uses the same
 * /api/order-register/invoices-by-date endpoint and inherits the
 * caller's scope. Defaults to yesterday+today (IST).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { BackBar } from "@/components/BackBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, RefreshCw, Package } from "lucide-react";
import { DocPrintBar, SelectBox, useDocSelection } from "@/components/DocPrintBar";
import { ReleaseInvoiceButton, ReleasedTag, ReleaseHistory } from "@/components/TodayInvExtras";

const isoYesterday = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};
const fmtDmy = (v) => {
  const s = String(v ?? "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

// Iter 198 — flow-stage hover tooltip with the generation timestamp
const STAGE_LABELS = { packing_list: "Packing List", bulk_data: "Bulk Data",
                        pack_team: "Pack Team", bulk_label: "Bulk Label" };
const fmtStageTooltip = (stage, inv) => {
  const done = (inv.stages || {})[stage];
  const at = (inv.stage_at || {})[stage];
  if (!done) return `${STAGE_LABELS[stage]} — not generated yet`;
  if (!at) return `${STAGE_LABELS[stage]} — generated`;
  const d = new Date(at);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${STAGE_LABELS[stage]} — generated ${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
};

export default function TodayInvoicesPage() {
  const tiSel = useDocSelection();
  const [params] = useSearchParams();
  const [from, setFrom] = useState(params.get("from") || isoYesterday());
  const [to, setTo] = useState(params.get("to") || isoToday());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: getAuthHeader() }).then(r => setIsAdmin(r.data?.role === "admin")).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/order-register/invoices-by-date`, {
        headers: getAuthHeader(),
        params: { date_from: from || undefined, date_to: to || undefined },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally { setBusy(false); }
  }, [from, to]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [load]);

  const download = async (fmt) => {
    try {
      const r = await axios.get(`${API}/order-register/invoices-report`, {
        headers: getAuthHeader(),
        params: { date_from: from || undefined, date_to: to || undefined, fmt },
        responseType: "blob",
      });
      const dispo = r.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `INVOICES.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Report download failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 pt-0 sm:p-6 sm:pt-0" data-testid="today-invoices-page">
      <BackBar title="Today's Invoices" />
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" /> Today's Invoices
          </h1>
          {data && (
            <span className="text-xs text-slate-600" data-testid="ti-range-label">
              Showing <b>{data.date_from === data.date_to ? data.date_from : `${data.date_from} → ${data.date_to}`}</b> (IST)
              {' '}· <b className="text-slate-800">{data.total_parcels ?? 0} parcels</b>
              {' '}({data.total_rows ?? 0} rows)
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => download("xlsx")}
                    disabled={!data || busy}
                    data-testid="ti-export-excel">
              <Download className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("pdf")}
                    disabled={!data || busy}
                    data-testid="ti-export-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap bg-white border rounded p-3">
          <Label className="text-xs">From</Label>
          <Input type="date" value={from}
                 onChange={(e) => setFrom(e.target.value)}
                 className="h-8 w-36" data-testid="ti-from" />
          <Label className="text-xs">To</Label>
          <Input type="date" value={to}
                 onChange={(e) => setTo(e.target.value)}
                 className="h-8 w-36" data-testid="ti-to" />
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid="ti-refresh">
            {busy ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</> : "Refresh"}
          </Button>
          <Button size="sm" variant="ghost" className="text-slate-500"
                  onClick={() => { setFrom(isoYesterday()); setTo(isoToday()); }}
                  data-testid="ti-y-today">Yesterday + Today</Button>
        </div>

        {data && (
          <>
            {data.invoices.length === 0 && data.pending_no_invoice === 0 ? (
              <div className="rounded border bg-white p-8 text-center text-slate-500" data-testid="ti-empty">
                No invoices in this date range.
              </div>
            ) : (
              <div className="bg-white border rounded overflow-hidden">
                <div className="p-2 border-b"><DocPrintBar keys={data.invoices.map(i => i.invoice_no)} sel={tiSel.sel} setSel={tiSel.setSel} testid="ti-docprint" /></div>
                <div className="max-h-[55vh] overflow-y-auto" data-testid="ti-invoices-list">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 w-8"></th>
                        <th className="px-3 py-2 text-left">Invoice #</th>
                        <th className="px-3 py-2 text-left w-28">Invoice Date</th>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">Company</th>
                        <th className="px-3 py-2 text-left w-20">Service</th>
                        <th className="px-1 py-2 text-center w-8" title="Packing List generated">PL</th>
                        <th className="px-1 py-2 text-center w-8" title="Bulk Data generated">BD</th>
                        <th className="px-1 py-2 text-center w-8" title="Pack Team print generated">PT</th>
                        <th className="px-1 py-2 text-center w-8" title="Bulk Label generated">BL</th>
                        <th className="px-3 py-2 text-center w-24">Completed</th>
                        <th className="px-3 py-2 text-center w-24">In Progress</th>
                        <th className="px-3 py-2 text-center w-20">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map(i => {
                        const done = i.status === 'completed';
                        return (
                          <tr key={i.invoice_key} className="border-t border-slate-100 hover:bg-slate-50"
                              data-testid={`ti-inv-row-${i.invoice_key}`}>
                            <td className="px-2 py-2 text-center"><SelectBox k={i.invoice_no} sel={tiSel.sel} toggle={tiSel.toggle} testid={`ti-select-${i.invoice_key}`} /></td>
                            <td className="px-3 py-2 font-mono font-semibold">{i.invoice_no}
                              <ReleasedTag inv={i} />
                              {i.in_flow && (
                                <span className="ml-1 px-1 rounded bg-blue-100 text-blue-700 text-[9px] font-sans font-semibold"
                                      title={i.flow_only ? "From Invoice Flow (no order rows in range)" : "Present in Invoice Flow for this range"}>Flow</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{fmtDmy(i.invoice_date) || '—'}</td>
                            <td className="px-3 py-2">{i.customer_name || '—'}</td>
                            <td className="px-3 py-2 text-violet-700 font-medium" data-testid={`today-inv-company-${i.invoice_key}`}>{i.company || '—'}</td>
                            <td className="px-3 py-2">{i.mode || '—'}</td>
                            {['packing_list', 'bulk_data', 'pack_team', 'bulk_label'].map(st => (
                              <td key={st} className="px-1 py-2 text-center"
                                  title={fmtStageTooltip(st, i)}
                                  data-testid={`ti-stage-${st}-${i.invoice_key}`}>
                                {(i.stages || {})[st]
                                  ? <span className="text-emerald-600 font-bold">✓</span>
                                  : <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-center">
                              {done ? <span className="inline-block px-2 py-0.5 rounded font-semibold text-[11px] bg-emerald-100 text-emerald-700">{i.parcel_count}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {!done ? <span className="inline-flex items-center gap-1"><span className="inline-block px-2 py-0.5 rounded font-semibold text-[11px] bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300">{i.parcel_count}</span>{isAdmin && <ReleaseInvoiceButton inv={i} onDone={load} />}</span> : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center text-slate-300">—</td>
                          </tr>
                        );
                      })}
                      {data.pending_no_invoice > 0 && (
                        <tr className="border-t border-rose-200 bg-rose-50" data-testid="ti-pending-row">
                          <td className="px-3 py-2 font-semibold text-rose-700" colSpan={10}>No Invoice (pending)</td>
                          <td className="px-3 py-2 text-center text-slate-300">—</td>
                          <td className="px-3 py-2 text-center text-slate-300">—</td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-block px-2 py-0.5 rounded font-semibold text-[11px] bg-rose-600 text-white">{data.pending_no_invoice}</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t sticky bottom-0">
                      <tr className="text-[11px] font-bold text-slate-600 uppercase">
                        <td className="px-3 py-2" colSpan={10}>
                          Total · {data.total_invoices} invoice{data.total_invoices === 1 ? '' : 's'} · {data.total_parcels ?? 0} parcel{(data.total_parcels ?? 0) === 1 ? '' : 's'}
                        </td>
                        <td className="px-3 py-2 text-center text-emerald-700">{data.completed}</td>
                        <td className="px-3 py-2 text-center text-yellow-800">{data.in_progress}</td>
                        <td className="px-3 py-2 text-center text-rose-700">{data.pending_no_invoice}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            <ReleaseHistory data={data} />
            {(data.pending_items || []).length > 0 && (
              <div className="bg-white border rounded overflow-hidden" data-testid="ti-pending-items">
                <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 text-xs font-bold text-rose-700 flex items-center justify-between">
                  <span>Pending Parcels ({data.pending_no_invoice}) — no invoice yet</span>
                  <span className="font-normal text-rose-500">
                    {data.pending_rows_total ?? 0} product row{(data.pending_rows_total ?? 0) === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="max-h-[45vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-rose-100/60 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left w-28">Code</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-left w-32">Country</th>
                        <th className="px-3 py-2 text-left">Tracking(s)</th>
                        <th className="px-3 py-2 text-center w-20">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pending_items.map((p, idx) => (
                        <tr key={idx} className="border-t border-rose-100 hover:bg-rose-50" data-testid={`ti-pending-item-${idx}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{p.code || '—'}</td>
                          <td className="px-3 py-2">{p.customer_name || '—'}</td>
                          <td className="px-3 py-2">{p.country || '—'}</td>
                          <td className="px-3 py-2 font-mono text-[11px]">
                            {(p.trackings || []).length > 0
                              ? p.trackings.join(', ')
                              : <span className="text-slate-400 italic">no tracking</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">{p.row_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
